/**
 * Flush — Theme Web Components
 * Native browser APIs only, no external dependencies.
 */

/* --- Cart Drawer ---
   Intercepts add-to-cart forms, calls Shopify's cart AJAX API,
   then refreshes the drawer contents and slides it open.
   Uses the native <dialog> element for focus trap + Escape handling. */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-cart-drawer]');
    if (!this.dialog) return;

    // Close triggers (close button, "continue shopping", backdrop click)
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-close]')) {
        this.close();
      }
      // Backdrop click — clicking on the dialog itself (not panel content) closes
      if (event.target === this.dialog) {
        this.close();
      }
    });

    // Qty +/- and remove
    this.addEventListener('click', (event) => {
      const inc = event.target.closest('[data-cart-drawer-increase]');
      const dec = event.target.closest('[data-cart-drawer-decrease]');
      const rem = event.target.closest('[data-cart-drawer-remove]');
      // Pass the action so focus can be restored to the matching control after the
      // drawer body is re-rendered (WCAG 2.4.3 / 3.2.2)
      if (inc) this.#changeQuantity(inc.dataset.lineKey, '+1', 'increase');
      if (dec) this.#changeQuantity(dec.dataset.lineKey, '-1', 'decrease');
      if (rem) this.#changeQuantity(rem.dataset.lineKey, 0, 'remove');
    });

    // Header / any external trigger to open
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-cart-drawer-open]');
      if (opener) {
        event.preventDefault();
        // Remember what opened us so focus can return there on close (WCAG 2.4.3)
        this.lastTrigger = opener;
        this.open();
      }
    });

    // Intercept add-to-cart form submits site-wide
    document.addEventListener('submit', this.#onAddToCart.bind(this));

    // Allow other scripts to force-refresh the drawer: window.dispatchEvent(new CustomEvent('cart:refresh'))
    window.addEventListener('cart:refresh', () => this.#refreshContents());
  }

  open() {
    if (!this.dialog) return;
    // M3.5: a second showModal() on an open modal throws in older Safari, which the add
    // handler would report as a failed add after a successful one.
    if (this.dialog.open) {
      this.#loadUpsell();
      return;
    }
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    document.body.style.overflow = 'hidden';
    this.#loadUpsell();
  }

  close() {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }
    document.body.style.overflow = '';
    // M1.11: a stale error or status must not show on the next open.
    this.#message(this.querySelector('[data-cart-drawer-error]'), '');
    this.#message(this.querySelector('[data-cart-drawer-status]'), '');
    // Return focus to whatever opened the drawer (WCAG 2.4.3) so it doesn't fall to <body>
    if (this.lastTrigger && this.lastTrigger.isConnected) {
      this.lastTrigger.focus();
    }
  }

  /* M1.11 (F35): errors are shown inline and announced; the shopper is never sent to
     Shopify's plain error page. Text is set with textContent only. */
  #pendingMessages = new WeakMap();

  #message(el, text) {
    if (!el) return;
    cancelAnimationFrame(this.#pendingMessages.get(el));
    el.textContent = '';
    if (!text) return;
    // Clear, then set on the next frame, so a repeat of the same text is announced again.
    this.#pendingMessages.set(
      el,
      requestAnimationFrame(() => {
        el.textContent = text;
      })
    );
  }

  #errorText(json, fallback) {
    const text = json && (typeof json.description === 'string' ? json.description : json.message);
    return typeof text === 'string' && text.trim() ? text.trim() : fallback;
  }

  #formError(form, submitBtn) {
    let el = form.querySelector('[data-cart-error]');
    if (!el) {
      // App or third-party add forms get the same inline alert after their button.
      el = document.createElement('p');
      el.className = 'product-page__cart-error';
      el.setAttribute('role', 'alert');
      el.setAttribute('data-cart-error', '');
      if (submitBtn) submitBtn.insertAdjacentElement('afterend', el);
      else form.appendChild(el);
    }
    return el;
  }

  async #onAddToCart(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = (form.getAttribute('action') || '').replace(/^\s+|\s+$/g, '');
    // Only intercept native Shopify add-to-cart forms
    if (!action.endsWith('/cart/add')) return;

    event.preventDefault();

    // M3.13: nothing chosen yet ("Choose a finish" state) means no variant: never send it.
    const idField = form.querySelector('[name="id"]');
    if (idField && !idField.value) return;

    // M3.1: the product form has two submit buttons, the in-flow one and the sticky
    // bar's (associated through form="ProductForm", so it sits outside the form in the
    // DOM). form.elements lists both. event.submitter tells which one was used; old
    // Safari lacks it, so fall back to the in-flow button.
    const submitters = Array.from(form.elements).filter((el) => el.type === 'submit');
    const submitBtn =
      event.submitter && event.submitter.form === form ? event.submitter : form.querySelector('[type="submit"]');
    // Guard double submit: a request is already in flight from either button.
    if (submitters.some((btn) => btn.dataset.cartBusy === 'true')) return;
    // Busy state uses aria-disabled, not the disabled property: disabling the focused
    // button would blur it to <body> and a keyboard user would lose their place (WCAG 2.4.3).
    // Every submitter of the form is marked, so a tap on the other button is ignored.
    const hadFocus = submitBtn != null && document.activeElement === submitBtn;
    // M3.5: an add from inside the drawer (complete the set) loses its form on re-render.
    const addedInDrawer = this.contains(form);
    for (const btn of submitters) {
      btn.setAttribute('aria-disabled', 'true');
      btn.dataset.cartBusy = 'true';
    }
    // The error goes next to the button that was used: the sticky bar has its own alert.
    const stickyBar = submitBtn ? submitBtn.closest('[data-sticky-atc]') : null;
    const stickyErrors = submitters
      .map((btn) => btn.closest('[data-sticky-atc]'))
      .filter(Boolean)
      .map((bar) => bar.querySelector('[data-cart-error]'));
    const formErrorEl = this.#formError(form, submitBtn && !stickyBar ? submitBtn : form.querySelector('[type="submit"]'));
    const errorEl = stickyBar ? stickyBar.querySelector('[data-cart-error]') || formErrorEl : formErrorEl;
    this.#message(formErrorEl, '');
    for (const el of stickyErrors) this.#message(el, '');
    // A success message from an earlier add must not sit in the dialog next to a failure.
    this.#message(this.querySelector('[data-cart-drawer-status]'), '');

    try {
      const formData = new FormData(form);
      formData.append('sections', this.#sectionId());
      formData.append('sections_url', `${window.location.pathname}${window.location.search}`);
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        console.error('Add to cart failed', err);
        // M3.6: gift card recipient errors come back as a per-field object (errors, or
        // description on some responses). Hand it to the form's recipient fields; the
        // general alert gets a theme sentence pointing at them, never the API's generic
        // message ("Cart Error") or "[object Object]". Only forms that carry the recipient
        // fieldset take this branch; any other object-shaped error falls through to the
        // plain message below.
        const fieldErrors = form.querySelector('gift-card-recipient')
          ? [err?.errors, err?.description].find((v) => v && typeof v === 'object')
          : null;
        if (fieldErrors) {
          form.dispatchEvent(new CustomEvent('gift-card-recipient:errors', { detail: { errors: fieldErrors } }));
          this.#message(errorEl, this.dataset.errorRecipient || this.dataset.errorAdd);
          return;
        }
        this.#message(errorEl, this.#errorText(err, this.dataset.errorAdd));
        return;
      }
      const data = await res.json();
      this.#message(this.querySelector('[data-cart-drawer-error]'), '');
      this.#applySectionHtml(data.sections);
      await this.#refreshContents();
      this.open();
      // Focus the new line so a keyboard user is not dropped to the top of the page.
      if (addedInDrawer) this.#restoreLineFocus(data.key, 'increase');
      const title = data.product_title || (data.items && data.items[0] && data.items[0].product_title);
      const added = title && this.dataset.added ? this.dataset.added.replace('[title]', () => title) : this.dataset.addedGeneric;
      this.#message(this.querySelector('[data-cart-drawer-status]'), added || '');
    } catch (e) {
      console.error('Add to cart error', e);
      this.#message(errorEl, this.dataset.errorAdd);
    } finally {
      for (const btn of submitters) {
        btn.removeAttribute('aria-disabled');
        delete btn.dataset.cartBusy;
      }
      if (submitBtn) {
        // If focus still fell to <body> (for example, the drawer did not open), put it back.
        const active = document.activeElement;
        if (hadFocus && submitBtn.isConnected && (!active || active === document.body)) {
          submitBtn.focus();
        }
      }
    }
  }

  async #changeQuantity(lineKey, delta, action) {
    if (!lineKey) return;
    // Ignore clicks while a change is pending (stops double +1 races).
    if (this.changing) return;
    this.changing = true;
    const errorEl = this.querySelector('[data-cart-drawer-error]');
    // The "added to your cart" status is stale once the shopper edits the cart.
    this.#message(this.querySelector('[data-cart-drawer-status]'), '');
    try {
      const currentItem = this.querySelector(`[data-line-key="${CSS.escape(lineKey)}"] [data-line-qty]`);
      const currentQty = currentItem ? parseInt(currentItem.textContent, 10) : 1;
      let newQty;
      if (delta === '+1') newQty = currentQty + 1;
      else if (delta === '-1') newQty = Math.max(0, currentQty - 1);
      else newQty = delta;

      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: newQty,
          sections: [this.#sectionId()],
          sections_url: `${window.location.pathname}${window.location.search}`,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        console.error('Cart change failed', err);
        // Leave the drawer body as it is; focus stays on the control that was used.
        this.#message(errorEl, this.#errorText(err, this.dataset.errorChange));
        return;
      }
      const data = await res.json();
      const line = (data.items || []).find((item) => item.key === lineKey);
      if (newQty > 0 && line && line.quantity < newQty && this.dataset.errorMax) {
        // Shopify capped the quantity at what is in stock.
        this.#message(errorEl, this.dataset.errorMax.replace('[quantity]', String(line.quantity)));
      } else {
        this.#message(errorEl, '');
      }
      this.#setCartCount(data.item_count);
      this.#applySectionHtml(data.sections);
      this.#restoreLineFocus(lineKey, action);
    } catch (e) {
      console.error('Quantity change error', e);
      this.#message(errorEl, this.dataset.errorChange);
    } finally {
      this.changing = false;
    }
  }

  #sectionId() {
    const wrap = this.closest('.shopify-section');
    return wrap?.id?.replace(/^shopify-section-/, '') || 'cart-drawer';
  }

  #setCartCount(count) {
    const value = count == null ? '' : String(count);
    for (const bubble of document.querySelectorAll('[data-cart-count-bubble]')) {
      bubble.textContent = value;
    }
  }

  #applySectionHtml(sections) {
    if (!sections) return;
    const html = sections[this.#sectionId()] || Object.values(sections)[0];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const freshBody = doc.querySelector('[data-cart-drawer-body]');
    const currentBody = this.querySelector('[data-cart-drawer-body]');
    if (freshBody && currentBody) {
      currentBody.replaceChildren(...freshBody.childNodes);
    }
    if (this.dialog?.open) this.#loadUpsell();
  }

  /* M3.5: complete the set. Runs only while the drawer is open, so a closed drawer costs
     no request. Complementary picks for the most recent line, then related; the section
     (sections/cart-upsell.liquid) applies the in-stock, not-in-cart and one-tap rules.
     Results are cached per anchor and cart contents; the previous request is aborted and a
     result for a placeholder that has been replaced is dropped. Failures stay silent. */
  #upsellCache = new Map();

  async #loadUpsell() {
    const slot = this.querySelector('[data-cart-upsell]');
    if (!slot || !slot.dataset.anchor || !slot.dataset.url) return;
    const key = `${slot.dataset.anchor}|${slot.dataset.exclude || ''}`;
    if (this.#upsellCache.has(key)) {
      this.#fillUpsell(slot, this.#upsellCache.get(key));
      return;
    }
    this.upsellController?.abort();
    const controller = new AbortController();
    this.upsellController = controller;
    const base = `${slot.dataset.url}?section_id=cart-upsell&product_id=${encodeURIComponent(slot.dataset.anchor)}&limit=6`;
    try {
      let nodes = await ProductRecommendations.fetchItems(`${base}&intent=complementary`, controller.signal, '.cart-upsell__results');
      if (!nodes[0]?.querySelector('[data-upsell-item]')) {
        nodes = await ProductRecommendations.fetchItems(`${base}&intent=related`, controller.signal, '.cart-upsell__results');
      }
      if (controller.signal.aborted || !slot.isConnected) return;
      const results = nodes[0] && nodes[0].querySelector('[data-upsell-item]') ? nodes[0] : null;
      if (this.#upsellCache.size >= 10) this.#upsellCache.delete(this.#upsellCache.keys().next().value);
      this.#upsellCache.set(key, results);
      this.#fillUpsell(slot, results);
    } catch (e) {
      console.warn('Complete the set did not load', e);
    }
  }

  #fillUpsell(slot, results) {
    if (!slot.isConnected) return;
    if (results) slot.replaceChildren(document.importNode(results, true));
    else slot.replaceChildren();
  }

  // After a drawer refresh, return keyboard focus to the control the user just used on
  // the same line. Falls back to nearby controls if the exact one is gone (line removed,
  // or decrement that hit zero and removed the line).
  #restoreLineFocus(lineKey, action) {
    if (!action) return;
    // A control we can actually move focus to. Disabled buttons can't receive focus
    // (e.g. the decrease button becomes disabled once a line drops to quantity 1).
    const firstFocusable = (root) => {
      if (!root) return null;
      for (const el of root.querySelectorAll('[data-cart-drawer-increase], [data-cart-drawer-decrease], [data-cart-drawer-remove]')) {
        if (!el.disabled) return el;
      }
      return null;
    };

    const line = this.querySelector(`[data-line-key="${CSS.escape(lineKey)}"]`);
    if (line) {
      const selectorByAction = {
        increase: '[data-cart-drawer-increase]',
        decrease: '[data-cart-drawer-decrease]',
        remove: '[data-cart-drawer-remove]',
      };
      // Prefer the same control; if it's gone or now disabled, fall back to another
      // enabled control on the same line.
      const same = line.querySelector(selectorByAction[action]);
      const target = same && !same.disabled ? same : firstFocusable(line);
      if (target) {
        target.focus();
        return;
      }
    }
    // The line is gone (removed, or quantity reached zero). Focus a sensible nearby element:
    // the first remaining line control, otherwise the close button, otherwise the drawer.
    const fallback =
      firstFocusable(this.querySelector('[data-cart-drawer-body]')) ||
      this.querySelector('[data-cart-drawer-close]') ||
      this.dialog;
    fallback?.focus();
  }

  async #refreshContents() {
    try {
      const sectionId = this.#sectionId();
      const sectionUrl = new URL(window.location.href);
      sectionUrl.searchParams.set('sections', sectionId);
      const [cartRes, sectionRes] = await Promise.all([
        fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        }),
        fetch(sectionUrl.toString(), {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        }),
      ]);
      if (!cartRes.ok || !sectionRes.ok) throw new Error('Drawer refresh failed');
      const cart = await cartRes.json();
      const sections = await sectionRes.json();
      this.#setCartCount(cart.item_count);
      this.#applySectionHtml(sections);
    } catch (e) {
      console.error('Drawer refresh error', e);
      this.#message(this.querySelector('[data-cart-drawer-error]'), this.dataset.errorRefresh);
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}


/* --- Announcement Bar ---
   M1.12: rotates message blocks every data-speed ms. Pauses on hover, on focus
   inside the bar, while the tab is hidden, and when the shopper presses the pause
   control (WCAG 2.2.2). No live region: rotating text is not announced. Inactive
   messages are visibility:hidden, so only the active one is in the a11y tree. */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.dismissBtn = this.querySelector('[data-dismiss-announcement]');
    this.bar = this.querySelector('.announcement-bar');
    // Keyed per section instance (RET-132) so multiple stacked announcement bars
    // dismiss independently instead of sharing one flag.
    this.storageKey = `announcement-dismissed-${this.id || 'default'}`;

    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(this.storageKey) === '1';
    } catch (e) {
      dismissed = false;
    }
    if (dismissed && this.bar) {
      this.bar.hidden = true;
    }

    this.dismissBtn?.addEventListener('click', this.#dismiss.bind(this));
    this.#setupRotation();
  }

  disconnectedCallback() {
    this.#stop();
    this.abort?.abort();
  }

  #setupRotation() {
    this.messages = [...this.querySelectorAll('[data-announcement-message]')];
    if (this.messages.length < 2) return;
    this.speed = parseInt(this.dataset.speed, 10) || 6000;
    this.toggle = this.querySelector('[data-announcement-toggle]');
    this.userPaused = false;
    this.hovered = false;
    this.focused = false;
    this.editorHold = false;
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.addEventListener('mouseenter', () => { this.hovered = true; this.#sync(); }, { signal });
    this.addEventListener('mouseleave', () => { this.hovered = false; this.#sync(); }, { signal });
    this.addEventListener('focusin', () => { this.focused = true; this.#sync(); }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) {
        this.focused = false;
        this.#sync();
      }
    }, { signal });
    document.addEventListener('visibilitychange', () => this.#sync(), { signal });

    this.toggle?.addEventListener('click', () => {
      this.userPaused = !this.userPaused;
      this.toggle.setAttribute('aria-pressed', String(this.userPaused));
      const label = this.userPaused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause;
      if (label) this.toggle.setAttribute('aria-label', label);
      this.#sync();
    }, { signal });

    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.messages.indexOf(event.target);
        if (index === -1) return;
        this.editorHold = true;
        this.#show(index);
        this.#sync();
      }, { signal });
      document.addEventListener('shopify:block:deselect', (event) => {
        if (!this.messages.includes(event.target)) return;
        this.editorHold = false;
        this.#sync();
      }, { signal });
    }

    this.#sync();
  }

  #shouldRun() {
    return !this.userPaused && !this.hovered && !this.focused && !this.editorHold && document.visibilityState !== 'hidden';
  }

  #sync() {
    if (this.#shouldRun()) this.#start();
    else this.#stop();
  }

  #start() {
    if (this.timer) return;
    this.timer = setInterval(() => this.#next(), this.speed);
  }

  #stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  #next() {
    const current = this.messages.findIndex((m) => m.classList.contains('is-active'));
    this.#show((current + 1) % this.messages.length);
  }

  #show(index) {
    this.messages.forEach((m, i) => m.classList.toggle('is-active', i === index));
  }

  #dismiss() {
    if (this.bar) {
      this.bar.hidden = true;
      try {
        sessionStorage.setItem(this.storageKey, '1');
      } catch (e) {
        /* storage blocked: the bar stays hidden for this page view only */
      }
      this.#stop();
    }
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* --- Header --- */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.mobileToggle = this.querySelector('[data-mobile-menu-toggle]');
    this.mobileClose = this.querySelector('[data-mobile-menu-close]');
    this.mobileNav = this.querySelector('#MobileNav');

    this.mobileToggle?.addEventListener('click', this.#openMobile.bind(this));
    this.mobileClose?.addEventListener('click', this.#closeMobile.bind(this));
    // Keep toggle state (and focus/overflow) in sync when the dialog closes via Escape
    // or backdrop, not just the close button (WCAG 4.1.2). Idempotent with #closeMobile.
    this.mobileNav?.addEventListener('close', this.#closeMobile.bind(this));
    // Clicking the backdrop (outside the drawer panel) closes it, matching the cart
    // drawer. The dialog box is only the drawer, so ::backdrop clicks target it. (DEL-232)
    this.mobileNav?.addEventListener('click', (event) => {
      if (event.target === this.mobileNav) this.#closeMobile();
    });

    for (const btn of this.querySelectorAll('.mobile-nav__expand')) {
      btn.addEventListener('click', this.#toggleMobileExpand.bind(this));
    }

    this.#setupDropdowns();
    this.#setupTileWarmup();
  }

  disconnectedCallback() {
    this.dropdownAbort?.abort();
    this.warmAbort?.abort();
  }

  /* M4.3: menu tile images are lazy, and a lazy image inside a closed panel or drawer
     is not fetched until it shows, so the first open painted empty tiles. Warm only the
     surface that is about to open, and only on intent, so nothing extra downloads on
     page load (M4.3 spec section 3):
     - desktop: when the pointer enters, presses or focus reaches a nav item that holds a
       mega panel, switch that panel's images to eager (an eager image loads while hidden);
     - phone: when the menu toggle is pressed or clicked, switch the drawer's images.
     There is no idle warm, so a page that never opens a menu fetches no menu tiles. */
  #setupTileWarmup() {
    this.warmAbort?.abort();
    this.warmAbort = new AbortController();
    const { signal } = this.warmAbort;
    const eager = (root) => {
      for (const img of root.querySelectorAll('img[loading="lazy"]')) img.loading = 'eager';
    };

    for (const panel of this.querySelectorAll('.header__dropdown--mega')) {
      const item = panel.closest('.header__nav-item') || panel;
      const warm = () => {
        eager(panel);
        for (const type of ['pointerenter', 'pointerdown', 'focusin']) item.removeEventListener(type, warm);
      };
      for (const type of ['pointerenter', 'pointerdown', 'focusin']) {
        item.addEventListener(type, warm, { signal, passive: true });
      }
    }

    if (this.mobileToggle && this.mobileNav) {
      const drawer = this.mobileNav;
      const toggle = this.mobileToggle;
      const warm = () => {
        eager(drawer);
        for (const type of ['pointerdown', 'click']) toggle.removeEventListener(type, warm);
      };
      for (const type of ['pointerdown', 'click']) {
        toggle.addEventListener(type, warm, { signal, passive: true });
      }
    }
  }

  /* M1.21: desktop Shop panel as a disclosure. The button toggles it on click (Enter
     and Space included); Escape closes it and returns focus to the button; tabbing
     out or clicking outside closes it. A close while the pointer is over the item adds
     is-dismissed so the resting mouse does not keep the hover panel open; mouseleave
     clears that. A close with the pointer elsewhere (outside click, Tab out) does not
     add it, since no mouseleave would follow to clear it and the next hover would fail. */
  #setupDropdowns() {
    this.dropdownAbort?.abort();
    this.dropdownAbort = new AbortController();
    const { signal } = this.dropdownAbort;
    const items = [...this.querySelectorAll('.header__nav-item--has-dropdown')];
    if (!items.length) return;

    const toggleOf = (item) => item.querySelector('[data-dropdown-toggle]');
    const setOpen = (item, open) => {
      const toggle = toggleOf(item);
      item.classList.toggle('is-open', open);
      toggle?.setAttribute('aria-expanded', String(open));
      item.classList.toggle('is-dismissed', !open && item.matches(':hover'));
    };

    for (const item of items) {
      const toggle = toggleOf(item);
      toggle?.addEventListener('click', () => {
        const open = !item.classList.contains('is-open');
        if (open) {
          for (const other of items) {
            if (other !== item && other.classList.contains('is-open')) setOpen(other, false);
          }
        }
        setOpen(item, open);
      }, { signal });

      item.addEventListener('focusout', (event) => {
        if (item.classList.contains('is-open') && !item.contains(event.relatedTarget)) setOpen(item, false);
      }, { signal });

      item.addEventListener('mouseleave', () => item.classList.remove('is-dismissed'), { signal });
    }

    this.querySelector('.header__nav')?.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const item = items.find((i) => i.classList.contains('is-open') || i.matches(':hover'));
      if (!item) return;
      setOpen(item, false);
      toggleOf(item)?.focus();
    }, { signal });

    document.addEventListener('click', (event) => {
      if (event.target.closest('.header__nav')) return;
      for (const item of items) {
        if (item.classList.contains('is-open')) setOpen(item, false);
      }
    }, { signal });
  }

  #openMobile() {
    this.mobileNav?.showModal();
    this.mobileToggle?.setAttribute('aria-expanded', 'true');
    if (this.mobileToggle?.dataset.labelClose) this.mobileToggle.setAttribute('aria-label', this.mobileToggle.dataset.labelClose);
    document.body.style.overflow = 'hidden';
  }

  #closeMobile() {
    this.mobileNav?.close();
    this.mobileToggle?.setAttribute('aria-expanded', 'false');
    if (this.mobileToggle?.dataset.labelOpen) this.mobileToggle.setAttribute('aria-label', this.mobileToggle.dataset.labelOpen);
    document.body.style.overflow = '';
    this.mobileToggle?.focus();
  }

  #toggleMobileExpand(event) {
    const btn = event.currentTarget;
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!expanded));
  }
}
customElements.define('site-header', SiteHeader);

/* --- Product Form ---
   M3.1: every variant-driven update (id, price, button, gallery, special order, pickup,
   sticky bar) runs through one #render(matched) so later items plug in there. */
class ProductForm extends HTMLElement {
  connectedCallback() {
    const variantJson = this.querySelector('[id^="ProductVariants-"]');
    if (!variantJson) return;

    this.variants = JSON.parse(variantJson.textContent);
    this.form = this.querySelector('[data-product-form]');
    if (!this.form) return;
    this.selects = this.querySelectorAll('[data-option-select]');
    this.atcButton = this.form.querySelector('.product-page__add-to-cart');

    // M3.2: per-variant { available, special } rendered by Liquid.
    const variantState = this.querySelector('[data-variant-state]');
    this.variantState = variantState ? JSON.parse(variantState.textContent) : {};
    // M3.13: "(special order)" suffixes only where special and regular variants mix.
    const states = Object.values(this.variantState);
    this.mixedSpecial = states.some((st) => st.special) && states.some((st) => st.available && !st.special);
    this.status = this.querySelector('[data-variant-status]');
    this.specialOrderNote = this.querySelector('[data-special-order-note]');
    this.specialOrderBadge = this.closest('.product-page')?.querySelector('[data-special-order-badge]');
    this.pickupAvailability = this.querySelector('[data-pickup-availability]');

    this.sticky = this.querySelector('[data-sticky-atc]');
    this.stickyButton = this.sticky?.querySelector('[data-sticky-button]');
    this.stickyPrice = this.sticky?.querySelector('[data-sticky-price]');
    this.stickySpecial = this.sticky?.querySelector('[data-sticky-special]');

    const idInput = this.form.querySelector('input[name="id"]');
    this.currentVariant = this.variants.find((v) => String(v.id) === String(idInput?.value)) || null;

    // Theme editor re-renders reconnect the element: drop the old listeners first.
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;

    for (const select of this.selects) {
      select.addEventListener('change', this.#onVariantChange.bind(this), { signal });
    }

    for (const btn of this.querySelectorAll('[data-qty-minus]')) {
      btn.addEventListener('click', this.#decrementQty.bind(this), { signal });
    }

    for (const btn of this.querySelectorAll('[data-qty-plus]')) {
      btn.addEventListener('click', this.#incrementQty.bind(this), { signal });
    }

    // Seal writes the chosen selling plan into the form. Its controls may set the value
    // without a change event, so re-read after clicks and key presses in its widget too.
    const syncPlanPrice = () => requestAnimationFrame(() => this.#syncSticky(this.currentVariant));
    this.form.addEventListener('change', syncPlanPrice, { signal });
    const purchaseOptions = this.form.querySelector('.product-page__purchase-options');
    purchaseOptions?.addEventListener('click', syncPlanPrice, { signal });
    purchaseOptions?.addEventListener('keyup', syncPlanPrice, { signal });

    this.#setupSticky(signal);
    this.#watchSealSlot(signal);

    // M3.13: announce shopper-made changes only (never on load).
    this.addEventListener('variant:change', (event) => {
      if (!event.detail?.initial) this.#announce(event.detail);
    }, { signal });

    // M3.2: bring the option states in line with Liquid (and with any values the browser
    // restored on reload or from the back-forward cache).
    this.#syncFromSelects({ initial: true });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.#syncFromSelects({ initial: true });
    }, { signal });
    // A preselected first in-stock variant (M3.2 setting) goes into the URL so apps that
    // read ?variant= (Seal) show the right plan price.
    if (this.hasAttribute('data-preselected') && this.currentVariant) this.#setUrlVariant(this.currentVariant.id);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.stickyObserver?.disconnect();
    this.dialogObserver?.disconnect();
    this.#setStickyShown(false);
  }

  /* M3.12: the Seal slot holds the widget's height until Seal renders into it. Seal
     fails on some pages (CORS error on its stylesheet); if nothing has rendered 4 s after
     load, drop the reserve rather than leave a permanent blank gap.
     r2: Seal first inserts its container at 0 px and paints the cards about 30 ms later,
     so releasing the reserve on the first child moved Add to cart up 226 px and back
     (CLS 0.05 on desktop). The reserve now stays until Seal's content is at least 80% of
     it tall (data-seal-ready), or 4 s after load at the latest.
     r3: the height is the span of the rendered children only (zero-size nodes such as a
     style tag, a script or a hidden element are skipped, so the height never goes
     negative). A widget that paints shorter than 80% of the reserve (a variant with no
     plans, one short plan line) releases once its height has held for 500 ms, instead of
     keeping a blank gap until the 4 s fallback. */
  #watchSealSlot(signal) {
    const slot = this.querySelector('.product-page__seal-slot');
    if (!slot) return;
    let settleTimer = 0;
    const ready = () => {
      if (slot.hasAttribute('data-seal-ready')) return;
      clearTimeout(settleTimer);
      slot.setAttribute('data-seal-ready', '');
      sizeObserver?.disconnect();
      childObserver.disconnect();
    };
    const contentHeight = () => {
      let top = Infinity;
      let bottom = -Infinity;
      for (const child of slot.children) {
        const rect = child.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) continue;
        top = Math.min(top, rect.top);
        bottom = Math.max(bottom, rect.bottom);
      }
      return bottom > top ? bottom - top : 0;
    };
    const measure = () => {
      if (slot.hasAttribute('data-seal-ready')) return;
      const reserve = parseFloat(getComputedStyle(slot).minHeight) || 0;
      const height = contentHeight();
      clearTimeout(settleTimer);
      if (height <= 0) return;
      if (height >= reserve * 0.8) {
        ready();
        return;
      }
      // Shorter than the reserve: wait until the height stops changing, then release.
      settleTimer = setTimeout(() => {
        if (slot.isConnected && contentHeight() > 0) ready();
      }, 500);
    };
    const sizeObserver = 'ResizeObserver' in window ? new ResizeObserver(measure) : null;
    const childObserver = new MutationObserver(() => {
      for (const child of slot.children) sizeObserver?.observe(child);
      measure();
    });
    childObserver.observe(slot, { childList: true });
    if (slot.firstElementChild) {
      for (const child of slot.children) sizeObserver?.observe(child);
      measure();
    }
    signal?.addEventListener('abort', () => { clearTimeout(settleTimer); sizeObserver?.disconnect(); childObserver.disconnect(); }, { once: true });
    const check = () => {
      setTimeout(() => {
        if (!slot.isConnected) return;
        if (!slot.firstElementChild) {
          slot.closest('[data-purchase-options]')?.setAttribute('data-seal-missing', '');
        } else {
          ready();
        }
      }, 4000);
    };
    if (document.readyState === 'complete') check();
    else window.addEventListener('load', check, { once: true, signal });
  }

  #onVariantChange(event) {
    const index = Number(event?.target?.dataset.optionIndex);
    this.#applyOptionStates(Number.isInteger(index) ? index : -1);
    this.#render(this.#matchSelection());
  }

  #syncFromSelects({ initial = false } = {}) {
    if (!this.selects.length) return;
    this.#applyOptionStates(-1);
    const matched = this.#matchSelection();
    if (!initial || !matched || !this.currentVariant || matched.id !== this.currentVariant.id) {
      this.#render(matched, { updateUrl: !initial });
    }
  }

  #selection() {
    return Array.from(this.selects, (s) => s.value);
  }

  #matchSelection() {
    const selected = this.#selection();
    // M3.13: an empty value is the "Choose a ..." placeholder: no variant yet.
    if (selected.includes('')) return null;
    return this.variants.find((v) => v.options.every((opt, i) => opt === selected[i])) || null;
  }

  /* M3.2: variants that fit the choices made in the selects before index (top down).
     M3.13: a select still on its placeholder is a wildcard. */
  #candidates(index, selected) {
    return this.variants.filter((v) => {
      for (let j = 0; j < index; j += 1) {
        if (selected[j] !== '' && v.options[j] !== selected[j]) return false;
      }
      return true;
    });
  }

  /* M3.2: recompute every select after changedIndex (all of them for -1). A value with no
     variant under the earlier choices is disabled ("not offered in X"); one whose variants
     are all unavailable reads "(sold out)". If a select's current value is no longer
     offered it moves to the first buyable value, else the first offered one, so an
     impossible combination can never be formed. Labels come from Liquid templates on the
     select; option.value is never changed. */
  #applyOptionStates(changedIndex) {
    const selects = Array.from(this.selects);
    for (let i = Math.max(0, changedIndex + 1); i < selects.length; i += 1) {
      const select = selects[i];
      const selected = this.#selection();
      const pool = this.#candidates(i, selected);
      const other = i > 0 ? selected[i - 1] : '';
      let firstBuyable = null;
      let firstOffered = null;
      for (const option of select.options) {
        const value = option.value;
        if (value === '') continue; // the "Choose a ..." placeholder keeps its text
        const matches = pool.filter((v) => v.options[i] === value);
        const exists = matches.length > 0;
        const buyable = matches.some((v) => v.available);
        option.disabled = !exists;
        option.textContent = this.#optionLabel(select, value, { exists, buyable, other, matches });
        if (exists && !firstOffered) firstOffered = option;
        if (buyable && !firstBuyable) firstBuyable = option;
      }
      const current = select.options[select.selectedIndex];
      // The placeholder is disabled on purpose (M3.13): leave a select that is still on it.
      if (current && current.value === '') continue;
      if (!current || current.disabled) {
        const next = firstBuyable || firstOffered;
        if (next) select.value = next.value;
      }
    }
  }

  /* Precedence: not offered, then sold out, then special order (M3.13, mixed products
     only, when every matching variant is special). */
  #optionLabel(select, value, { exists, buyable, other, matches }) {
    const fill = (template) => (template || '[value]').replace('[value]', () => value).replace('[other]', () => other);
    if (!exists) return fill(select.dataset.labelNotOffered);
    if (!buyable) return fill(select.dataset.labelSoldOut);
    if (this.mixedSpecial && matches.every((v) => this.#isSpecial(v.id))) return fill(select.dataset.labelSpecialOrder);
    return value;
  }

  #setUrlVariant(id) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (e) {
      /* URL update is a convenience only */
    }
  }

  /* The single place that brings the page in line with a variant (or with no match). */
  #render(matched, { updateUrl = true } = {}) {
    this.currentVariant = matched;
    const atcBtn = this.atcButton;
    const priceEl = this.querySelector('.product-page__price');

    // M3.13: "Choose a finish" state. Some select is still on its placeholder: no id,
    // the From price stays, the button stays disabled and names the first open option.
    const openSelect = Array.from(this.selects).find((sel) => sel.value === '');
    if (!matched && openSelect) {
      const optionName = openSelect.dataset.optionName || '';
      const idInput = this.form.querySelector('input[name="id"]');
      if (idInput) idInput.value = '';
      if (atcBtn) {
        atcBtn.disabled = true;
        atcBtn.textContent = (atcBtn.dataset.chooseText || '').replace('[option]', () => optionName);
      }
      if (this.pickupAvailability) this.pickupAvailability.hidden = true;
      this.#syncSticky(null);
      this.#announceChange(null, { choose: optionName, initial: !updateUrl });
      return;
    }

    // Defensive only since M3.2 (the option states make impossible combinations
    // unreachable): no variant for the selection means nothing can be added, and no
    // price or special-order state from an earlier variant may stay on screen.
    if (!matched) {
      if (atcBtn) {
        atcBtn.disabled = true;
        atcBtn.textContent = atcBtn.dataset.unavailableText;
      }
      if (priceEl) {
        priceEl.removeAttribute('aria-label');
        priceEl.textContent = atcBtn?.dataset.unavailableText || '';
      }
      if (this.specialOrderNote) this.specialOrderNote.hidden = true;
      if (this.specialOrderBadge) this.specialOrderBadge.hidden = true;
      if (this.pickupAvailability) this.pickupAvailability.hidden = true;
      this.#syncSticky(null);
      this.#announceChange(null, { initial: !updateUrl });
      return;
    }

    // Apps that follow the variant (Seal) listen for change on the id input.
    const idInput = this.form.querySelector('input[name="id"]');
    if (idInput && String(idInput.value) !== String(matched.id)) {
      idInput.value = matched.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (priceEl) this.#renderPrice(priceEl, matched);

    if (atcBtn) {
      atcBtn.disabled = !matched.available;
      atcBtn.textContent = matched.available ? atcBtn.dataset.addText : atcBtn.dataset.soldText;
    }

    // The variant's own photo, else the product's first photo, so a variant without an
    // image never keeps the previous variant's picture.
    const imageSrc = matched.featured_image?.src || this.dataset.defaultImage;
    if (imageSrc) this.closest('.product-page')?.querySelector('image-gallery')?.showImage(imageSrc, matched.featured_image);

    // Pickup contradicts the special-order lead time and a variant that cannot be bought
    // must not offer free pickup. Flush has one location, so otherwise pickup is on.
    if (this.pickupAvailability) {
      this.pickupAvailability.hidden = this.#isSpecial(matched.id) || !matched.available;
    }

    this.#updateSpecialOrder(matched.id);
    this.#syncSticky(matched);
    if (updateUrl) this.#setUrlVariant(matched.id);
    this.#announceChange(matched, { initial: !updateUrl });
  }

  #renderPrice(priceEl, variant) {
    const price = this.#formatMoney(variant.price);
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const original = this.#formatMoney(variant.compare_at_price);
      const sale = document.createElement('span');
      sale.className = 'product-page__price-sale';
      sale.textContent = price;
      const compare = document.createElement('s');
      compare.className = 'product-page__price-compare';
      compare.setAttribute('aria-hidden', 'true');
      compare.textContent = original;
      priceEl.replaceChildren(sale, compare);
      const template = priceEl.dataset.saleLabel;
      if (template) {
        priceEl.setAttribute('aria-label', template.replace('[sale]', () => price).replace('[original]', () => original));
      }
    } else {
      const span = document.createElement('span');
      span.textContent = price;
      priceEl.replaceChildren(span);
      priceEl.removeAttribute('aria-label');
    }
  }

  /* M3.2: the hook M3.13 builds on (live region, special-order labels). */
  #announceChange(variant, { choose = '', initial = false } = {}) {
    const state = variant ? this.variantState[variant.id] || null : null;
    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant, state, choose, initial } }));
  }

  /* M3.13: one polite sentence per settled change: options, price, status. Cleared and
     then set on the next frame so a repeat is read again; debounced 150 ms so arrow keys
     in a closed select (each fires change on Windows) announce only where they land.
     All wording comes from locale templates on the region. */
  #announce({ variant, state, choose } = {}) {
    const region = this.status;
    if (!region) return;
    clearTimeout(this.announceTimer);
    cancelAnimationFrame(this.announceFrame);
    region.textContent = '';
    this.announceTimer = setTimeout(() => {
      const d = region.dataset;
      const options = this.#selection().filter(Boolean).join(', ');
      let status;
      let price = '';
      if (variant) {
        if (state?.special || this.#isSpecial(variant.id)) status = d.statusSpecial;
        else status = variant.available ? d.statusAvailable : d.statusSoldOut;
        price = this.#formatMoney(variant.price);
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const template = this.querySelector('.product-page__price')?.dataset.saleLabel;
          if (template) {
            price = template.replace('[sale]', () => this.#formatMoney(variant.price)).replace('[original]', () => this.#formatMoney(variant.compare_at_price));
          }
        }
      } else if (choose) {
        status = (d.statusChoose || '').replace('[option]', () => choose);
      } else {
        status = d.statusUnavailable;
      }
      let text;
      if (price) text = (d.template || '').replace('[options]', () => options).replace('[price]', () => price).replace('[status]', () => status || '');
      else if (options) text = (d.templateShort || '').replace('[options]', () => options).replace('[status]', () => status || '');
      else text = status || '';
      this.announceFrame = requestAnimationFrame(() => {
        region.textContent = text;
      });
    }, 150);
  }

  /* True only on variants of a product tagged "Special Order" that are buyable at zero
     stock (from the data-variant-state map). */
  #isSpecial(variantId) {
    return this.variantState?.[variantId]?.special === true;
  }

  #updateSpecialOrder(variantId) {
    const isSpecial = this.#isSpecial(variantId);
    if (this.specialOrderNote) this.specialOrderNote.hidden = !isSpecial;
    if (this.specialOrderBadge) this.specialOrderBadge.hidden = !isSpecial;
  }

  /* M3.1: the sticky bar mirrors the main button (state and text), shows the price for
     the chosen purchase option and the special-order tag. */
  #syncSticky(variant) {
    if (!this.sticky) return;
    if (this.stickyButton && this.atcButton) {
      this.stickyButton.disabled = this.atcButton.disabled;
      this.stickyButton.textContent = this.atcButton.textContent.trim();
    }
    if (this.stickyPrice) {
      // No variant (M3.13 choice state or no match): mirror the page price (From ...).
      const pagePrice = this.querySelector('.product-page__price')?.textContent.trim() || '';
      this.stickyPrice.textContent = variant ? this.#formatMoney(this.#displayPrice(variant)) : pagePrice;
    }
    if (this.stickySpecial) {
      this.stickySpecial.hidden = !(variant && this.#isSpecial(variant.id));
    }
  }

  /* Subscription aware: with a Seal selling plan chosen, the plan's allocation price
     from the variant JSON; otherwise (or if it is not found) the variant price. */
  #displayPrice(variant) {
    const plan = this.form ? new FormData(this.form).get('selling_plan') : null;
    if (plan && Array.isArray(variant.selling_plan_allocations)) {
      const allocation = variant.selling_plan_allocations.find((a) => String(a.selling_plan_id) === String(plan));
      if (allocation && typeof allocation.price === 'number') return allocation.price;
    }
    return variant.price;
  }

  /* M3.1 bar, M3.12 trigger: the bar shows on phones whenever the main Add to cart is
     out of view (below the fold on load, or scrolled past), with the sticky header's
     height taken off the top edge. The observer fires once on observe(), which covers
     first load. It stays hidden while any dialog is open (cart drawer, lightbox, phone
     menu), while a text field of the product form has focus (on-screen keyboard), and
     at 990 px and up. */
  #setupSticky(signal) {
    if (!this.sticky || !this.atcButton || !('IntersectionObserver' in window)) return;
    this.phoneQuery = window.matchMedia('(max-width: 989px)');
    this.#observeAtc();
    this.phoneQuery.addEventListener('change', () => this.#updateSticky(), { signal });

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.#observeAtc(), 200);
    }, { signal });

    // Dialogs toggle their open attribute: one observer for all of them, no polling.
    this.dialogObserver?.disconnect();
    this.dialogObserver = new MutationObserver(() => this.#updateSticky());
    this.dialogObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
    signal.addEventListener('abort', () => this.dialogObserver?.disconnect());

    const isTextField = (el) =>
      el instanceof HTMLTextAreaElement ||
      (el instanceof HTMLInputElement && !['hidden', 'checkbox', 'radio', 'submit', 'button', 'reset', 'image'].includes(el.type));
    this.addEventListener('focusin', (event) => {
      if (!isTextField(event.target) || this.sticky.contains(event.target)) return;
      this.textFocus = true;
      this.#updateSticky();
    }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!isTextField(event.target)) return;
      this.textFocus = false;
      this.#updateSticky();
    }, { signal });
  }

  #observeAtc() {
    const header = document.querySelector('.header');
    const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    this.stickyObserver?.disconnect();
    this.stickyObserver = new IntersectionObserver((entries) => {
      this.atcEntry = entries[entries.length - 1];
      this.#updateSticky();
    }, { rootMargin: `-${top}px 0px 0px 0px`, threshold: 0 });
    this.stickyObserver.observe(this.atcButton);
  }

  #shouldShowSticky(entry) {
    if (!entry || !this.phoneQuery?.matches) return false;
    return !entry.isIntersecting;
  }

  #stickyBlocked() {
    return Boolean(this.textFocus || document.querySelector('dialog[open]'));
  }

  #updateSticky() {
    this.#setStickyShown(!this.#stickyBlocked() && this.#shouldShowSticky(this.atcEntry));
  }

  #setStickyShown(show) {
    const bar = this.sticky;
    if (!bar || show === Boolean(this.stickyShown)) return;
    this.stickyShown = show;
    // A hidden bar must not keep focus: hand it to the main button.
    if (!show && bar.contains(document.activeElement)) this.atcButton?.focus();
    bar.classList.toggle('is-visible', show);
    bar.toggleAttribute('inert', !show);
    if (show) bar.removeAttribute('aria-hidden');
    else bar.setAttribute('aria-hidden', 'true');
    // M3.9 reads --sticky-atc-offset to lift the Smile and Inbox launchers.
    const root = document.documentElement;
    if (show) root.style.setProperty('--sticky-atc-offset', `${bar.offsetHeight}px`);
    root.classList.toggle('has-sticky-atc', show);
  }

  #decrementQty() {
    const input = this.querySelector('[data-qty-input]');
    if (input && parseInt(input.value) > 1) input.value = parseInt(input.value) - 1;
  }

  #incrementQty() {
    const input = this.querySelector('[data-qty-input]');
    if (input) input.value = parseInt(input.value) + 1;
  }

  #formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
}
customElements.define('product-form', ProductForm);

/* --- Gift card recipient (M3.6) ---
   Shopify's native recipient form. The checkbox and fieldset render disabled; this
   enables the checkbox, and the fields only while it is ticked, so nothing is sent for
   an unticked add. Server per-field errors arrive from CartDrawer as a
   gift-card-recipient:errors event on the form. */
class GiftCardRecipient extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-recipient-toggle]');
    this.fields = this.querySelector('[data-recipient-fields]');
    if (!this.toggle || !this.fields) return;
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.toggle.disabled = false;
    const offset = this.querySelector('[data-recipient-offset]');
    if (offset) offset.value = String(new Date().getTimezoneOffset());

    // Local dates (not toISOString, which shifts to UTC): today to 90 days ahead.
    const sendOn = this.#field('send_on');
    if (sendOn) {
      const today = new Date();
      const max = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 90);
      sendOn.min = this.#ymd(today);
      sendOn.max = this.#ymd(max);
      sendOn.addEventListener('invalid', () => this.#setError('send_on', this.dataset.errorSendOn), { signal });
    }
    this.#field('email')?.addEventListener('invalid', () => this.#setError('email', this.dataset.errorEmail), { signal });

    this.toggle.addEventListener('change', () => this.#sync(true), { signal });
    this.#sync(false);

    const message = this.#field('message');
    const count = this.querySelector('[data-message-count]');
    const live = this.querySelector('[data-message-live]');
    if (message && count) {
      let wasLow = false;
      const update = () => {
        const remaining = Math.max(0, (Number(message.maxLength) || 200) - message.value.length);
        const text = (count.dataset.template || '').replace('[remaining]', String(remaining));
        count.textContent = text;
        // Announce only when crossing into the last 20 characters (not on every key).
        const low = remaining <= 20;
        if (live && low !== wasLow) live.textContent = low ? text : '';
        wasLow = low;
      };
      message.addEventListener('input', update, { signal });
      update();
    }

    for (const key of ['email', 'name', 'message', 'send_on']) {
      this.#field(key)?.addEventListener('input', () => this.#setError(key, ''), { signal });
    }

    this.form = this.closest('form');
    this.form?.addEventListener('submit', () => this.#clearErrors(), { signal });
    this.form?.addEventListener('gift-card-recipient:errors', (event) => this.#showErrors(event.detail?.errors), { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  #field(key) {
    return this.querySelector(`[data-recipient-field="${key}"]`);
  }

  #ymd(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  #sync(moveFocus) {
    const on = this.toggle.checked;
    this.fields.hidden = !on;
    this.fields.disabled = !on;
    this.toggle.setAttribute('aria-expanded', String(on));
    // Typed values stay, so ticking again restores them.
    if (on && moveFocus) this.#field('email')?.focus();
    if (!on) this.#clearErrors();
  }

  #setError(key, text) {
    const input = this.#field(key);
    const errorEl = input?.closest('.gift-recipient__field')?.querySelector('.gift-recipient__error');
    if (!input || !errorEl) return;
    errorEl.textContent = text || '';
    errorEl.hidden = !text;
    if (text) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
  }

  #clearErrors() {
    for (const key of ['email', 'name', 'message', 'send_on']) this.#setError(key, '');
  }

  #showErrors(errors) {
    if (!errors || typeof errors !== 'object') return;
    const fallback = { email: this.dataset.errorEmail, send_on: this.dataset.errorSendOn, message: this.dataset.errorMessage };
    let first = null;
    for (const key of ['email', 'name', 'message', 'send_on']) {
      if (!(key in errors)) continue;
      const value = errors[key];
      const text = Array.isArray(value) ? value.join(' ') : typeof value === 'string' ? value : fallback[key] || '';
      this.#setError(key, text);
      if (!first) first = this.#field(key);
    }
    first?.focus();
  }
}
if (!customElements.get('gift-card-recipient')) {
  customElements.define('gift-card-recipient', GiftCardRecipient);
}

/* --- Image Thumbnails --- */
class ImageGallery extends HTMLElement {
  #warmed = new Set();
  #lightbox = null;
  #lbImg = null;
  #lb = { scale: 1, x: 0, y: 0 };

  #phone = null;
  #slideIndex = 0;

  connectedCallback() {
    for (const thumb of this.querySelectorAll('[data-thumbnail]')) {
      thumb.addEventListener('click', this.#onThumbClick.bind(this));
      thumb.addEventListener('pointerenter', () => this.#warmThumb(thumb), { once: true });
      thumb.addEventListener('focus', () => this.#warmThumb(thumb), { once: true });
    }
    this.mainSizes = this.querySelector('#ProductFeaturedImage')?.getAttribute('sizes') || '';
    this.#scheduleWarmNext();
    this.#setupZoom();
    this.#setupSlides();
  }

  disconnectedCallback() {
    this.slidesAbort?.abort();
    this.slideObserver?.disconnect();
  }

  /* M3.1: phone swipe gallery. Below 990 px the main image is slide 1 of a scroll-snap
     track. An IntersectionObserver on the track tracks the current slide for the dots or
     counter and the arrow states. The desktop gallery never uses any of this. */
  #setupSlides() {
    this.track = this.querySelector('[data-slides]');
    if (!this.track) return;
    this.slides = [this.querySelector('[data-slide-main]'), ...this.querySelectorAll('[data-slide]')].filter(Boolean);
    this.#phone = window.matchMedia('(max-width: 989px)');
    this.slidesAbort?.abort();
    this.slidesAbort = new AbortController();
    const { signal } = this.slidesAbort;
    if (this.slides.length < 2) return;
    this.#syncTrackSemantics();

    this.prevBtn = this.querySelector('[data-slide-prev]');
    this.nextBtn = this.querySelector('[data-slide-next]');
    this.dots = [...this.querySelectorAll('[data-slide-dot]')];
    this.counter = this.querySelector('[data-slide-counter]');

    if ('IntersectionObserver' in window) {
      this.slideObserver?.disconnect();
      this.slideObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const index = this.slides.indexOf(entry.target);
            if (index > -1) this.#setSlideIndex(index);
          }
        },
        { root: this.track, threshold: 0.6 }
      );
      this.slides.forEach((slide) => this.slideObserver.observe(slide));
    }

    // Arrows never reach the lightbox (they sit outside the track, and stop the event anyway).
    this.prevBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.#goToSlide(this.#slideIndex - 1);
    }, { signal });
    this.nextBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.#goToSlide(this.#slideIndex + 1);
    }, { signal });

    // The track is focusable: Left and Right move one slide, Enter opens the lightbox on
    // the current slide (native horizontal scroll keys also work).
    this.track.addEventListener('keydown', (e) => {
      if (e.target !== this.track || !this.#phone.matches) return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        this.#goToSlide(this.#slideIndex + (e.key === 'ArrowRight' ? 1 : -1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        this.#openLightbox(this.#slideImage(this.#slideIndex));
      }
    }, { signal });

    // Tapping a slide opens the lightbox on that image. A swipe fires no click.
    for (const slide of this.querySelectorAll('[data-slide]')) {
      slide.addEventListener('click', () => this.#openLightbox(slide.querySelector('img')), { signal });
    }

    // Crossing 990 px: put the track back on slide 1 and let the desktop code run.
    this.#phone.addEventListener('change', () => {
      this.track.scrollLeft = 0;
      this.#setSlideIndex(0);
      this.#syncTrackSemantics();
    }, { signal });
  }

  /* The track is a carousel (focusable region with its own keys) only while the phone
     layout is active. At 990 px and up it is a plain block around the zoomable main
     image, so it carries no role, label or tab stop there. */
  #syncTrackSemantics() {
    const track = this.track;
    if (!track) return;
    if (this.#phone?.matches) {
      track.setAttribute('tabindex', '0');
      track.setAttribute('role', 'region');
      track.setAttribute('aria-roledescription', 'carousel');
      if (track.dataset.slidesLabel) track.setAttribute('aria-label', track.dataset.slidesLabel);
    } else {
      for (const attr of ['tabindex', 'role', 'aria-roledescription', 'aria-label']) track.removeAttribute(attr);
      if (document.activeElement === track) track.blur();
    }
  }

  #slideImage(index) {
    const slide = this.slides?.[index];
    if (!slide) return this.mainImage;
    return slide.hasAttribute('data-slide-main') ? this.mainImage : slide.querySelector('img');
  }

  #goToSlide(index) {
    if (!this.track || !this.slides) return;
    const target = Math.max(0, Math.min(this.slides.length - 1, index));
    this.track.scrollTo({ left: target * this.track.clientWidth });
    this.#setSlideIndex(target);
  }

  #setSlideIndex(index) {
    this.#slideIndex = index;
    const total = this.slides.length;
    this.dots?.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    if (this.counter) this.counter.textContent = `${index + 1} / ${total}`;
    this.slides.forEach((slide, i) => {
      if (i === index) slide.setAttribute('aria-current', 'true');
      else slide.removeAttribute('aria-current');
    });
    this.prevBtn?.setAttribute('aria-disabled', String(index === 0));
    this.nextBtn?.setAttribute('aria-disabled', String(index === total - 1));
  }

  /* M3.7: after load plus idle, warm only the image after the active one (not every
     image at 1200 px). Phones skip it: the swipe slides are lazy images of their own. */
  #scheduleWarmNext() {
    const thumbs = [...this.querySelectorAll('[data-thumbnail]')];
    if (thumbs.length < 2) return;
    const schedule = () => {
      const run = () => this.#warmNext();
      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 1500 });
      else setTimeout(run, 300);
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
  }

  #warmNext() {
    if (this.#phone?.matches) return;
    const thumbs = [...this.querySelectorAll('[data-thumbnail]')];
    if (thumbs.length < 2) return;
    const active = thumbs.findIndex((t) => t.getAttribute('aria-current') === 'true');
    this.#warmThumb(thumbs[(active + 1) % thumbs.length]);
  }

  #warmThumb(thumb) {
    if (!thumb) return;
    this.#warmImage(thumb.dataset.imageUrl, thumb.dataset.imageSrcset);
  }

  /* Warm the candidate the browser will actually pick: sizes, then srcset, then src, with
     the main image's strings, so the swap finds that file in the cache. */
  #warmImage(url, srcset) {
    const key = srcset || url;
    if (!key || this.#warmed.has(key)) return;
    this.#warmed.add(key);
    const img = new Image();
    if (srcset) {
      if (this.mainSizes) img.sizes = this.mainSizes;
      img.srcset = srcset;
    }
    if (url) img.src = url;
  }

  #onThumbClick(event) {
    this.#activate(event.currentTarget);
  }

  /* Called by ProductForm when the selected variant has its own image. */
  showImage(src, meta) {
    if (!src) return;
    const base = src.split('?')[0];
    // M3.1: on phones scroll the swipe track to the matching slide. Slide 1's picture is
    // never swapped there, which would show one image twice.
    if (this.#phone?.matches && this.slides?.length > 1) {
      const index = this.slides.findIndex((slide) => (slide.dataset.imageUrl || '').split('?')[0] === base);
      if (index > -1) this.#goToSlide(index);
      return;
    }
    for (const thumb of this.querySelectorAll('[data-thumbnail]')) {
      if ((thumb.dataset.imageUrl || '').split('?')[0] === base) {
        this.#activate(thumb);
        return;
      }
    }
    const sep = src.includes('?') ? '&' : '?';
    this.#setMainImage(`${src}${sep}width=1200`);
    // M3.12: keep the reserved box in step with the new picture.
    if (meta && meta.width && meta.height) this.#setImageBox(meta.width / meta.height, meta.width);
  }

  /* M3.12: --ar and --img-w size the main image box (theme.css). */
  #setImageBox(aspect, width) {
    const wrap = this.querySelector('.product-page__main-image');
    if (!wrap || !(aspect > 0)) return;
    wrap.style.setProperty('--ar', String(aspect));
    if (width > 0) wrap.style.setProperty('--img-w', `${width}px`);
  }

  #activate(thumb) {
    const url = thumb.dataset.imageUrl;
    if (!url) return;
    this.#setMainImage(url, thumb.dataset.imageSrcset);
    this.#setImageBox(parseFloat(thumb.dataset.imageAspect), parseFloat(thumb.dataset.imageWidth));
    // M1.9: the main image alt follows the thumbnail or variant swap (the lightbox copies it).
    const mainImg = this.querySelector('#ProductFeaturedImage');
    if (mainImg && thumb.dataset.imageAlt) mainImg.alt = thumb.dataset.imageAlt;
    for (const t of this.querySelectorAll('[data-thumbnail]')) {
      t.classList.remove('product-page__thumb--active');
      t.setAttribute('aria-current', 'false');
    }
    thumb.classList.add('product-page__thumb--active');
    thumb.setAttribute('aria-current', 'true');
    this.#warmNext();
  }

  /* M3.7: a thumbnail swap keeps sizes and sets the thumb's srcset, so a phone loads
     the phone-sized file, not the 1200. Without a srcset (a variant image with no thumb)
     the srcset is stripped, since the browser resolves srcset over src. The 2048 zoom
     prewarm is gone: the magnifier layers the current image under the 2048. */
  #setMainImage(url, srcset) {
    const mainImg = this.querySelector('#ProductFeaturedImage');
    if (!mainImg) return;
    if (srcset) {
      if (this.mainSizes) mainImg.sizes = this.mainSizes;
      mainImg.srcset = srcset;
    } else {
      mainImg.removeAttribute('srcset');
      mainImg.removeAttribute('sizes');
    }
    mainImg.src = url;
  }

  /* DEL-223: PDP image zoom.
     Desktop (fine pointer + hover): an inline magnifier that follows the cursor.
     All devices: click, tap, or Enter opens a full-screen lightbox with wheel and pinch
     zoom and drag pan. A larger source is loaded on demand, derived from the current main
     image each time so it tracks variant and thumbnail swaps; the default page load is
     unchanged. */
  #setupZoom() {
    this.mainImage = this.querySelector('#ProductFeaturedImage');
    this.mainWrap = this.querySelector('.product-page__main-image');
    if (!this.mainImage || !this.mainWrap) return;

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.#enableMagnifier();
    }

    this.mainWrap.classList.add('product-page__main-image--zoomable');
    this.mainWrap.setAttribute('role', 'button');
    this.mainWrap.setAttribute('tabindex', '0');
    this.mainWrap.setAttribute('aria-label', this.dataset.zoomLabel || 'Zoom product image');
    this.mainWrap.addEventListener('click', () => this.#openLightbox());
    this.mainWrap.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.#openLightbox();
      }
    });
  }

  #highResUrl(width, src) {
    src = src || this.mainImage.currentSrc || this.mainImage.src;
    if (!src) return '';
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&]width=)\d+/, `$1${width}`);
    return src + (src.includes('?') ? '&' : '?') + 'width=' + width;
  }

  #enableMagnifier() {
    const img = this.mainImage;
    const pane = document.createElement('div');
    pane.className = 'product-page__zoom-pane';
    pane.setAttribute('aria-hidden', 'true');
    this.mainWrap.appendChild(pane);
    const ZOOM = 2.2;

    const setPosition = (e) => {
      const r = img.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
      const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
      pane.style.backgroundPosition = `${x * 100}% ${y * 100}%`;
    };

    img.addEventListener('mouseenter', (e) => {
      const url = this.#highResUrl(2048);
      if (!url) return;
      // M3.7: the loaded main image sits under the 2048 layer, so the pane shows the
      // picture at once (no white flash) and sharpens when the 2048 arrives.
      const current = img.currentSrc || img.src;
      pane.style.backgroundImage = current && current !== url ? `url("${url}"), url("${current}")` : `url("${url}")`;
      pane.style.backgroundSize = `${ZOOM * 100}%`;
      pane.style.left = `${img.offsetLeft}px`;
      pane.style.top = `${img.offsetTop}px`;
      pane.style.width = `${img.offsetWidth}px`;
      pane.style.height = `${img.offsetHeight}px`;
      setPosition(e); // start from the entry point so the view does not jump
      this.mainWrap.classList.add('is-zooming');
      pane.classList.add('is-visible');
    });
    img.addEventListener('mousemove', setPosition);
    img.addEventListener('mouseleave', () => {
      pane.classList.remove('is-visible');
      this.mainWrap.classList.remove('is-zooming');
    });
  }

  /* M3.1: opens on the image that was tapped (a phone slide or the main image). */
  #openLightbox(img = this.mainImage) {
    const source = img || this.mainImage;
    if (!source) return;
    const url = this.#highResUrl(2560, source.currentSrc || source.src);
    if (!url) return;
    const dlg = this.#ensureLightbox();
    this.#lbImg.alt = source.alt || '';
    this.#lbImg.src = url;
    this.#lbSetTransform(1, 0, 0);
    dlg.showModal();
  }

  #ensureLightbox() {
    if (this.#lightbox) return this.#lightbox;
    const dlg = document.createElement('dialog');
    dlg.className = 'product-page__lightbox';
    dlg.innerHTML =
      '<button type="button" class="product-page__lightbox-close" aria-label="">&times;</button>' +
      '<div class="product-page__lightbox-stage"><img class="product-page__lightbox-img" alt=""></div>';
    document.body.appendChild(dlg);
    this.#lightbox = dlg;
    this.#lbImg = dlg.querySelector('.product-page__lightbox-img');
    dlg.querySelector('.product-page__lightbox-close').setAttribute('aria-label', this.dataset.closeLabel || 'Close');
    const stage = dlg.querySelector('.product-page__lightbox-stage');

    const pointers = new Map();
    let startDist = 0;
    let startScale = 1;
    let panStart = null;
    let downPt = null;
    let moved = false;

    dlg.querySelector('.product-page__lightbox-close').addEventListener('click', () => dlg.close());
    dlg.addEventListener('close', () => this.#lbSetTransform(1, 0, 0));

    stage.addEventListener('wheel', (e) => {
      e.preventDefault();
      const next = Math.min(4, Math.max(1, this.#lb.scale - Math.sign(e.deltaY) * 0.25));
      this.#lbSetTransform(next, this.#lb.x, this.#lb.y);
    }, { passive: false });

    stage.addEventListener('pointerdown', (e) => {
      stage.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      moved = false;
      downPt = { x: e.clientX, y: e.clientY };
      if (pointers.size === 2) {
        const p = [...pointers.values()];
        startDist = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
        startScale = this.#lb.scale;
      } else {
        panStart = { x: e.clientX - this.#lb.x, y: e.clientY - this.#lb.y };
      }
    });
    stage.addEventListener('pointermove', (e) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (downPt && Math.hypot(e.clientX - downPt.x, e.clientY - downPt.y) > 6) moved = true;
      if (pointers.size === 2 && startDist) {
        const p = [...pointers.values()];
        const dist = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
        this.#lbSetTransform(Math.min(4, Math.max(1, startScale * (dist / startDist))), this.#lb.x, this.#lb.y);
      } else if (pointers.size === 1 && panStart && this.#lb.scale > 1) {
        this.#lbSetTransform(this.#lb.scale, e.clientX - panStart.x, e.clientY - panStart.y);
      }
    });
    const release = (e) => {
      const singleTap = pointers.size === 1 && !moved;
      pointers.delete(e.pointerId);
      if (pointers.size < 2) startDist = 0;
      if (singleTap) this.#tapZoom(e, stage);
      else if (pointers.size === 0 && this.#lb.scale === 1) this.#lbSetTransform(1, 0, 0);
    };
    stage.addEventListener('pointerup', release);
    stage.addEventListener('pointercancel', (e) => {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) startDist = 0;
    });

    return dlg;
  }

  /* A tap that did not drag: on the image, toggle zoom (in centred on the tap point,
     or back out); off the image (the letterbox margin), close. The close button stays
     visible throughout, so there is always a way out. */
  #tapZoom(e, stage) {
    const ir = this.#lbImg.getBoundingClientRect();
    const onImage = e.clientX >= ir.left && e.clientX <= ir.right && e.clientY >= ir.top && e.clientY <= ir.bottom;
    if (!onImage) {
      this.#lightbox.close();
      return;
    }
    if (this.#lb.scale > 1) {
      this.#lbSetTransform(1, 0, 0);
      return;
    }
    const rect = stage.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const s = 2.5;
    this.#lbSetTransform(s, (e.clientX - cx) * (1 - s), (e.clientY - cy) * (1 - s));
  }

  #lbSetTransform(scale, x, y) {
    this.#lb = { scale, x, y };
    if (!this.#lbImg) return;
    this.#lbImg.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    this.#lbImg.style.cursor = scale > 1 ? 'zoom-out' : 'zoom-in';
  }
}
customElements.define('image-gallery', ImageGallery);

/* --- Product recommendations (M3.4) ---
   Loads when the row comes near the viewport (so it costs nothing above the fold):
   complementary picks, then related, then the same vendor, each rendered by the
   product-recommendations section. De-duplicated, the current product left out, sold-out
   related or vendor cards used only to fill the row. Vendor cards in the current Scent
   move to the front, and every card with that scent links straight to its variant. */
class ProductRecommendations extends HTMLElement {
  /* Fetch a section render and return its [data-recommendation-item] nodes. A failed or
     non-OK request is an empty tier; it never throws. Shared with the cart upsell. */
  static async fetchItems(url, signal, selector = '[data-recommendation-item]') {
    if (!url) return [];
    try {
      const res = await fetch(url, { signal, headers: { Accept: 'text/html' } });
      if (!res.ok) return [];
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      return [...doc.querySelectorAll(selector)];
    } catch (e) {
      return [];
    }
  }

  connectedCallback() {
    this.grid = this.querySelector('[data-recommendations-grid]');
    if (!this.grid || this.started) return;
    this.abort?.abort();
    this.abort = new AbortController();
    if (!('IntersectionObserver' in window)) {
      this.#load();
      return;
    }
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer.disconnect();
      this.#load();
    }, { rootMargin: '0px 0px 400px 0px' });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.abort?.abort();
    this.selectAbort?.abort();
  }

  async #load() {
    if (this.started) return;
    this.started = true;
    const { signal } = this.abort;
    const limit = parseInt(this.dataset.limit, 10) || 4;
    const currentId = this.dataset.productId;
    const [complementary, related, vendor] = await Promise.all([
      ProductRecommendations.fetchItems(this.dataset.complementaryUrl, signal),
      ProductRecommendations.fetchItems(this.dataset.relatedUrl, signal),
      ProductRecommendations.fetchItems(this.dataset.vendorUrl, signal),
    ]);
    if (signal.aborted) return;

    const scent = this.#currentScent();
    if (scent) {
      // Same-scent vendor cards first (the wash leads to the lotion in its scent).
      const hasScent = (item) => Object.prototype.hasOwnProperty.call(this.#scentMap(item), scent);
      vendor.sort((a, b) => Number(hasScent(b)) - Number(hasScent(a)));
    }

    const seen = new Set([currentId]);
    const picked = [];
    const spare = [];
    const add = (items, soldOutAsSpare, tier) => {
      for (const item of items) {
        const id = item.dataset.productId;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        // The source tier stays on the card so QA can tell which list supplied it.
        item.dataset.recTier = tier;
        if (soldOutAsSpare && item.querySelector('.product-card--sold-out')) spare.push(item);
        else picked.push(item);
      }
    };
    add(complementary, false, 'complementary');
    add(related, true, 'related');
    add(vendor, true, 'vendor');
    const items = picked.concat(spare).slice(0, limit);
    this.dataset.tierCounts = `complementary:${complementary.length} related:${related.length} vendor:${vendor.length}`;

    if (!items.length) {
      // Nothing to show: no empty heading or reserved gap.
      (this.closest('.shopify-section') || this).remove();
      return;
    }
    this.grid.replaceChildren(...items.map((item) => document.importNode(item, true)));
    this.grid.classList.remove('product-recommendations__grid--loading');
    this.grid.setAttribute('aria-busy', 'false');
    this.#linkScent(scent);
    this.#followScentSelect();
  }

  #scentMap(item) {
    const raw = item.querySelector('[data-scent-variants]')?.dataset.scentVariants;
    if (!raw) return {};
    try {
      return JSON.parse(raw) || {};
    } catch (e) {
      return {};
    }
  }

  #scentSelect() {
    return document.querySelector('product-form [data-option-select][data-option-name="scent"]');
  }

  #currentScent() {
    const select = this.#scentSelect();
    return (select && select.value) || this.dataset.scent || '';
  }

  /* Point each card link at the variant in the current scent when it has one. */
  #linkScent(scent) {
    for (const card of this.grid.querySelectorAll('[data-scent-variants]')) {
      const link = card.querySelector('.product-card__link');
      if (!link) continue;
      const url = new URL(link.getAttribute('href'), window.location.origin);
      const id = scent ? this.#scentMap(card)[scent] : null;
      if (id) url.searchParams.set('variant', id);
      else url.searchParams.delete('variant');
      link.setAttribute('href', url.pathname + url.search);
    }
  }

  #followScentSelect() {
    const select = this.#scentSelect();
    if (!select) return;
    this.selectAbort?.abort();
    this.selectAbort = new AbortController();
    select.addEventListener('change', () => this.#linkScent(select.value), { signal: this.selectAbort.signal });
  }
}
if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

/* --- Collection Sort --- */
class CollectionSort extends HTMLElement {
  connectedCallback() {
    this.querySelector('[data-sort-select]')?.addEventListener('change', (event) => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      window.location.href = url.toString();
    });
  }
}
customElements.define('collection-sort', CollectionSort);

/* --- Collection Filters --- */
class CollectionFilters extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    // Present only on the mobile drawer (RET-109); null once the dialog is rendered
    // as the static desktop sidebar, which is what keeps the two behaviours apart.
    this.dialog = this.closest('[data-filters-dialog]');
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.form?.addEventListener('change', this.#onFilterChange.bind(this), { signal });
    // M3.3: Enter in a price field applies the filters (keeps sort_by, drops page) rather
    // than doing nothing or a plain GET. Two number fields and no submit button block
    // implicit submission, so listen for the key as well as submit.
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.apply();
    }, { signal });
    this.form?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || !event.target.matches?.('[data-filter-price]')) return;
      event.preventDefault();
      this.apply();
    }, { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  #onFilterChange() {
    // Inside the open mobile drawer, batch instead of navigating: reloading on every
    // tap would close the drawer and force the shopper to reopen it for each facet.
    // "Show results" commits them together. Desktop keeps applying immediately.
    if (this.dialog?.open) {
      this.#scheduleCount();
      return;
    }
    this.apply();
  }

  /* M3.14: while the drawer is open, the apply button counts the results the pending
     choices would give: a debounced, abortable Section Rendering request for this
     section, whose server-rendered (pluralized) label is copied into the button and a
     status line. Desktop never gets here (it applies on change). */
  #scheduleCount() {
    clearTimeout(this.countTimer);
    this.countTimer = setTimeout(() => this.#fetchCount(), 350);
  }

  async #fetchCount() {
    const button = this.dialog?.querySelector('[data-filters-apply]');
    const status = this.dialog?.querySelector('[data-filters-status]');
    const sectionId = this.dialog?.dataset.sectionId;
    if (!button || !sectionId) return;
    this.countController?.abort();
    const controller = new AbortController();
    this.countController = controller;
    const url = this.#pendingUrl();
    url.searchParams.set('section_id', sectionId);
    button.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(url.toString(), { signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(`Count request failed: ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const label = doc.querySelector('[data-filters-apply]')?.textContent.trim();
      if (!label) throw new Error('No count in the section response');
      button.textContent = label;
      if (status) status.textContent = label;
    } catch (error) {
      if (error.name === 'AbortError') return;
      button.textContent = button.dataset.labelFallback || button.textContent;
      if (status) status.textContent = '';
    } finally {
      if (this.countController === controller) button.removeAttribute('aria-busy');
    }
  }

  /* Called when the drawer opens (store the label) and when it closes without applying
     (restore it and drop any pending request). */
  rememberCount() {
    const button = this.dialog?.querySelector('[data-filters-apply]');
    if (button) this.initialCountLabel = button.textContent;
  }

  cancelCount() {
    clearTimeout(this.countTimer);
    this.countController?.abort();
    const button = this.dialog?.querySelector('[data-filters-apply]');
    const status = this.dialog?.querySelector('[data-filters-status]');
    if (button) {
      button.removeAttribute('aria-busy');
      if (this.initialCountLabel) button.textContent = this.initialCountLabel;
    }
    if (status) status.textContent = '';
  }

  #pendingUrl() {
    const url = this.#urlWithoutFilters();
    for (const [name, value] of new FormData(this.form).entries()) {
      if (value) url.searchParams.append(name, value);
    }
    return url;
  }

  // Rebuild the URL from the form's checked values. Non-filter params such as sort_by
  // survive; page is dropped because the result set changed.
  apply() {
    if (!this.form) return;
    this.committing = true;
    clearTimeout(this.countTimer);
    this.countController?.abort();
    window.location.href = this.#pendingUrl().toString();
  }

  clear() {
    this.committing = true;
    window.location.href = this.#urlWithoutFilters().toString();
  }

  /* M3.3: closing the drawer without Show results or Clear all drops unapplied ticks. */
  resetUnapplied() {
    if (this.committing) return;
    this.form?.reset();
    this.cancelCount();
  }

  #urlWithoutFilters() {
    const url = new URL(window.location.href);
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.')) {
        url.searchParams.delete(key);
      }
    }
    // Reset to page 1 whenever the result set changes
    url.searchParams.delete('page');
    return url;
  }
}
customElements.define('collection-filters', CollectionFilters);

/* Mobile collection filter drawer (RET-109).
   The filters sit in a <dialog>, so the browser handles focus trapping, Escape and the
   backdrop. Desktop never opens it, so every listener here is inert there. */
(() => {
  const getDialog = () => document.querySelector('[data-filters-dialog]');
  const getFilters = () => document.querySelector('collection-filters');
  let lastTrigger = null;

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-filters-open]');
    if (opener) {
      const dialog = getDialog();
      if (!dialog) return;
      event.preventDefault();
      // Remember the trigger so focus returns to it on close (WCAG 2.4.3)
      lastTrigger = opener;
      getFilters()?.rememberCount();
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      document.body.style.overflow = 'hidden';
      return;
    }

    const dialog = getDialog();
    if (!dialog || !dialog.open) return;

    // Backdrop click: the dialog itself is the target when the click misses the panel
    if (event.target === dialog || event.target.closest('[data-filters-close]')) {
      dialog.close();
      return;
    }
    if (event.target.closest('[data-filters-apply]')) {
      getFilters()?.apply();
      return;
    }
    if (event.target.closest('[data-filters-clear]')) {
      getFilters()?.clear();
    }
  });

  // Fires for Escape and for close() alike, so scroll and focus are always restored.
  // 'close' does not bubble, hence the capture phase.
  document.addEventListener(
    'close',
    (event) => {
      if (!event.target.matches?.('[data-filters-dialog]')) return;
      document.body.style.overflow = '';
      // M3.3: ticks that were not applied must not show when the drawer reopens.
      getFilters()?.resetUnapplied();
      if (lastTrigger && lastTrigger.isConnected) {
        lastTrigger.focus();
      }
    },
    true
  );

  // M3.3: crossing into the desktop layout (tablet rotation) with the drawer open would
  // leave the page inert behind a static sidebar in the top layer: close it.
  const desktop = window.matchMedia('(min-width: 990px)');
  desktop.addEventListener('change', (event) => {
    const dialog = getDialog();
    if (event.matches && dialog?.open) dialog.close();
  });
})();

/* M4.6: the search page "Filter and sort" disclosure is the sidebar at 750 px and up
   (kept open) and a closed row on phones, so results stay above the fold. */
(() => {
  const panels = document.querySelectorAll('[data-search-filters]');
  if (!panels.length) return;
  const wide = window.matchMedia('(min-width: 750px)');
  const sync = () => panels.forEach((panel) => { panel.open = wide.matches; });
  sync();
  wide.addEventListener('change', sync);
})();

/* --- FAQ Accordion (single-open + smooth height animation) --- */
(function initFaqAccordion() {
  const duration = 300;
  const easing = 'cubic-bezier(0.4, 0, 0.2, 1)';

  function animate(wrap, from, to, onDone) {
    wrap.style.overflow = 'hidden';
    wrap.style.height = from + 'px';
    wrap.getBoundingClientRect();
    wrap.style.transition = `height ${duration}ms ${easing}`;
    wrap.style.height = to + 'px';
    const cleanup = () => {
      wrap.style.transition = '';
      wrap.style.height = '';
      wrap.style.overflow = '';
      wrap.removeEventListener('transitionend', cleanup);
      if (onDone) onDone();
    };
    wrap.addEventListener('transitionend', cleanup, { once: true });
    setTimeout(cleanup, duration + 50);
  }

  function close(details) {
    const wrap = details.querySelector('.faq-section__answer-wrap');
    if (!wrap) { details.open = false; return; }
    animate(wrap, wrap.scrollHeight, 0, () => { details.open = false; });
  }

  function open(details) {
    const wrap = details.querySelector('.faq-section__answer-wrap');
    details.open = true;
    if (!wrap) return;
    const targetHeight = wrap.scrollHeight;
    animate(wrap, 0, targetHeight);
  }

  function setup() {
    const items = document.querySelectorAll('[data-faq-accordion]');
    items.forEach((details) => {
      const summary = details.querySelector('summary');
      if (!summary || summary.dataset.faqBound) return;
      summary.dataset.faqBound = '1';
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        const isOpen = details.open;
        if (isOpen) {
          close(details);
          return;
        }
        items.forEach((other) => {
          if (other !== details && other.open) close(other);
        });
        open(details);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();

/* --- Reveal-on-scroll (subtle fade + lift as sections enter the viewport) --- */
(function initReveal() {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion || !('IntersectionObserver' in window)) return;

  // Only target sections inside the main content area. Header/footer groups stay put —
  // transforming a sticky element's ancestor breaks `position: sticky`.
  const main = document.getElementById('MainContent');
  if (!main) return;

  const targets = main.querySelectorAll(':scope > .shopify-section');
  const filtered = Array.from(targets).filter((el) => {
    // Don't fade the hero: it sits under the translucent header and any transform
    // would fight our negative-margin overlap trick.
    if (el.querySelector('.hero-banner')) return false;
    // M3.12: nor the product section. Its will-change: transform would make it the
    // containing block of the fixed sticky bar, and opacity 0 delays the main image paint.
    if (el.querySelector('.product-page')) return false;
    return true;
  });

  filtered.forEach((el) => el.classList.add('reveal'));

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal--visible');
          io.unobserve(entry.target);
        }
      }
    },
    { root: null, rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
  );

  filtered.forEach((el) => io.observe(el));
})();

/* --- Header Group Height (exposed as --header-group-offset for hero overlap) ---
   Shopify renders each section in a `{% sections %}` group as its OWN sibling
   at the body level (with the group class applied to each). So we have to sum
   the heights of all elements matching `.shopify-section-group-header-group`
   rather than reading the offsetHeight of a single wrapper. */
(function initHeaderGroupHeight() {
  const root = document.documentElement;

  function update() {
    const groups = document.querySelectorAll('.shopify-section-group-header-group');
    if (!groups.length) return;
    // Measure from viewport 0 to the bottom of the last group element —
    // more accurate than summing offsetHeights (handles fixed/sticky children too).
    const lastBottom = groups[groups.length - 1].getBoundingClientRect().bottom;
    root.style.setProperty('--header-group-offset', `${Math.max(0, Math.round(lastBottom))}px`);
    // Header-only and announcement-only heights, so the hero can sit just
    // below the announcement bar while the header floats over its top.
    const headerEl = document.querySelector('.header');
    if (headerEl) root.style.setProperty('--header-offset', `${Math.round(headerEl.getBoundingClientRect().height)}px`);
    // Sum every announcement bar (RET-132 can stack more than one), not just the first —
    // a hidden/dismissed bar naturally contributes 0 via its [hidden] display:none.
    let annHeight = 0;
    document.querySelectorAll('.announcement-bar').forEach((el) => {
      annHeight += el.getBoundingClientRect().height;
    });
    root.style.setProperty('--announcement-offset', `${Math.round(annHeight)}px`);
    // Pinned-bar height only (RET-132), so the sticky header can dock directly
    // beneath whichever bars are actually pinned, rather than always at 0.
    let pinnedHeight = 0;
    document.querySelectorAll('.announcement-bar--pinned').forEach((el) => {
      pinnedHeight += el.getBoundingClientRect().height;
    });
    root.style.setProperty('--pinned-announcement-offset', `${Math.round(pinnedHeight)}px`);
  }

  update();

  // Re-measure on window resize + on load (images may have resized by then)
  let resizeRAF;
  const schedule = () => {
    cancelAnimationFrame(resizeRAF);
    resizeRAF = requestAnimationFrame(update);
  };
  window.addEventListener('resize', schedule);
  window.addEventListener('load', schedule);

  // Re-measure when the announcement bar is dismissed/restored
  if ('MutationObserver' in window) {
    const mo = new MutationObserver(schedule);
    document.querySelectorAll('.shopify-section-group-header-group').forEach((g) => {
      mo.observe(g, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'class', 'style'] });
    });
  }
})();

/* --- Cart page quantity steppers ---
   The +/- buttons on the /cart page had no handler (the drawer is the primary
   cart, but the page is still directly reachable). Wire them to adjust the
   matching quantity input and submit the cart form so line totals update. */
(function initCartPageQty() {
  const root = window.Shopify?.routes?.root || '/';

  async function changeLine(id, quantity) {
    const res = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id, quantity }),
    });
    if (!res.ok) throw new Error('Cart change failed');
    window.location.reload();
  }

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-cart-page-remove]');
    if (remove) {
      event.preventDefault();
      changeLine(remove.getAttribute('data-cart-page-remove'), 0).catch((err) => {
        console.error(err);
        window.location.href = remove.href;
      });
      return;
    }

    const minus = event.target.closest('[data-cart-qty-minus]');
    const plus = event.target.closest('[data-cart-qty-plus]');
    if (!minus && !plus) return;

    const button = minus || plus;
    const key = button.getAttribute(minus ? 'data-cart-qty-minus' : 'data-cart-qty-plus');
    const input = document.querySelector('[data-cart-qty-input="' + key + '"]');
    if (!input) return;

    event.preventDefault();
    const current = parseInt(input.value, 10) || 0;
    const next = minus ? Math.max(0, current - 1) : current + 1;
    if (next === current) return;
    changeLine(key, next).catch((err) => {
      console.error(err);
      input.value = next;
      const form = input.closest('form');
      if (form) form.submit();
    });
  });
})();


/* --- Transparent header over hero (solidifies once the hero scrolls past) --- */
(function initHeaderHeroOverlay() {
  const header = document.querySelector('.header');
  const hero = document.querySelector('.hero-banner');
  if (!header || !hero) return;
  function update() {
    const heroBottom = hero.getBoundingClientRect().bottom;
    const headerBottom = header.getBoundingClientRect().bottom;
    header.classList.toggle('is-over-hero', heroBottom > headerBottom);
  }
  update();
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  window.addEventListener('load', update);
})();

/* --- Local actions (M1.18) ---
   Elements tagged data-local-action (call | directions | hours) with a
   data-local-action-placement publish one custom event per click, so a custom pixel
   (held for Millar, D10) or Clarity (D3) can record calls, directions and hours taps.
   The pixel clicked event does not carry data attributes, hence the custom event.
   Never calls preventDefault: the link still dials or opens maps. The document
   CustomEvent 'flush:local-action' is the test hook. */
(function initLocalActions() {
  const TYPES = ['call', 'directions', 'hours'];
  document.addEventListener('click', (event) => {
    const el = event.target.closest('[data-local-action]');
    if (!el) return;
    const type = el.getAttribute('data-local-action');
    if (!TYPES.includes(type)) return;
    const detail = {
      type,
      placement: (el.getAttribute('data-local-action-placement') || 'unknown').slice(0, 40),
      path: window.location.pathname,
    };
    document.dispatchEvent(new CustomEvent('flush:local-action', { detail }));
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('flush:local_action', detail);
      }
      if (typeof window.clarity === 'function') window.clarity('event', 'local_' + type);
    } catch (e) {
      /* never block the tap */
    }
  });
})();

/* --- Brand marquee: pause toggle (touch and keyboard users; hover and focus pause in CSS) --- */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-marquee-toggle]');
  if (!toggle) return;
  const root = toggle.closest('.brand-marquee');
  if (!root) return;
  const paused = root.classList.toggle('is-paused');
  toggle.setAttribute('aria-pressed', String(paused));
});
